import './RtuTreeList.css'; // Importing CSS for styling

import { AppDispatch, RootState } from '../../redux/store/store';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Avatar } from 'primereact/avatar';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { RTU } from '../../types/RTU';
import RTUActions from '../RTUActions';
import { SkeletonRegion, SkeletonTree } from '../common/Skeleton/Skeleton';
import { Tag } from 'primereact/tag';
import { TreeTable } from 'primereact/treetable';
import createTreeData from './TreeData/createTreeData';
import { defaultThemeColor } from '../../config/themeConfig';
import { fetchRTUs } from '../../redux/slices/rtuSlice';
import { buildAssetStatusFilterOptions, getDisplayStatusFromRecord, getStatusColor, getStatusLabelKey } from '../../utils/statusConfig';
import { humanizeStatusReason, humanizeStatusSource } from '../../utils/statusDisplayConfig';
import { PaginatorRowsLabel, ROWS_PER_PAGE_OPTIONS, STANDARD_PAGE_REPORT_TEMPLATE, STANDARD_PAGINATOR_TEMPLATE } from '../../utils/paginatorConfig';
import { formatDisplayDateTime } from '../../utils/dateTimeFormat';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const COLUMN_VISIBILITY_STORAGE_PREFIX = 'dms_column_visibility';
const GROUPED_ASSET_VIEW_KEY = 'grouped-asset-list';

const resolveColumnVisibilityUserKey = (): string => {
    if (typeof window === 'undefined') {
        return 'anonymous';
    }

    try {
        const rawUser = window.localStorage.getItem('dms_auth_user');
        if (rawUser) {
            const parsed = JSON.parse(rawUser) as Record<string, unknown>;
            const stableIdentity =
                parsed.username ??
                parsed.logins ??
                parsed.email ??
                parsed.id ??
                parsed.employeeId;
            if (stableIdentity !== undefined && stableIdentity !== null) {
                return String(stableIdentity);
            }
        }
    } catch (error) {
        console.warn('Failed to parse dms_auth_user for column persistence key', error);
    }

    return 'anonymous';
};

const getColumnVisibilityStorageKey = (viewKey: string): string =>
    `${COLUMN_VISIBILITY_STORAGE_PREFIX}:${viewKey}:${resolveColumnVisibilityUserKey()}`;

const hydrateColumnVisibility = <T extends { field: string; visible: boolean }>(
    columns: T[],
    storageKey: string
): T[] => {
    if (typeof window === 'undefined') {
        return columns;
    }

    try {
        const stored = window.localStorage.getItem(storageKey);
        if (!stored) {
            return columns;
        }
        const parsed = JSON.parse(stored) as Record<string, boolean>;
        const hydrated = columns.map((column) =>
            Object.prototype.hasOwnProperty.call(parsed, column.field)
                ? { ...column, visible: Boolean(parsed[column.field]) }
                : column
        );
        if (hydrated.every((column) => !column.visible)) {
            console.warn('Stored column visibility hid every column; restoring defaults');
            return columns;
        }
        return hydrated;
    } catch (error) {
        console.warn('Failed to hydrate column visibility settings', error);
        return columns;
    }
};

const persistColumnVisibility = <T extends { field: string; visible: boolean }>(
    columns: T[],
    storageKey: string
) => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const payload = columns.reduce<Record<string, boolean>>((accumulator, column) => {
            accumulator[column.field] = Boolean(column.visible);
            return accumulator;
        }, {});
        window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (error) {
        console.warn('Failed to persist column visibility settings', error);
    }
};

const RTUTreeList = () => {
    const { t } = useTranslation();
    const { rtus, loading } = useSelector((state: RootState) => state.rtu || {});
    const dispatch = useDispatch<AppDispatch>();
    const columnStorageKeyRef = useRef<string>(getColumnVisibilityStorageKey(GROUPED_ASSET_VIEW_KEY));
    const hasHydratedColumnsRef = useRef(false);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [statusFilterValue, setStatusFilterValue] = useState<string | null>(null);
    const searchableTreeData = React.useMemo(() => {
        let rows = Array.isArray(rtus) ? rtus : [];
        if (statusFilterValue) {
            rows = rows.filter(
                (rtu: RTU) => getDisplayStatusFromRecord(rtu) === statusFilterValue
            );
        }
        const query = globalFilter.trim().toLowerCase();
        if (!query) {
            return rows;
        }
        return rows.filter((rtu: RTU) =>
            [
                rtu.id,
                rtu.name,
                rtu.location,
                rtu.status,
                rtu.statusSource,
                rtu.statusReason,
                rtu.configVersion,
                rtu.deploymentVersion,
                rtu.publicIPAddress,
                rtu.localIPAddress,
                rtu.modemIP,
                rtu.ethernetIP,
                rtu.provider,
                rtu.field,
                rtu.groupId,
            ]
                .filter((value) => value !== undefined && value !== null)
                .join(' ')
                .toLowerCase()
                .includes(query)
        );
    }, [globalFilter, rtus, statusFilterValue]);
    const treeData = React.useMemo(() => {
        return createTreeData(searchableTreeData);
      }, [searchableTreeData]);
    const rtuMap = React.useMemo(() => {
        const map: { [key: number]: RTU } = {};
        rtus.forEach((rtu: RTU) => {
            map[rtu.id] = rtu;
        });
        return map;
    }, [rtus]);
    
    const toggleFilters = () => {
        setShowFilters((previous) => {
            const next = !previous;
            setColumns((currentColumns) =>
                currentColumns.map((column) => ({
                    ...column,
                    filter: next,
                }))
            );
            return next;
        });
    };
    const [filterMode] = useState<'lenient' | 'strict'>('lenient');
    const [showFilters, setShowFilters] = useState(false); // Ensure filters are shown
    const [selectedNodeKeys, setSelectedNodeKeys] = useState(null);



    const statusOptions = useMemo(() => buildAssetStatusFilterOptions(t), [t]);

    // State for dialog visibility and selected file

    const selectedRows = React.useMemo(() => {
        const selectedKeys = Object.keys(selectedNodeKeys ?? {});
        return selectedKeys
            .map((key) => Number(key))
            .filter((id) => Number.isFinite(id) && Boolean(rtuMap[id]))
            .map((id) => rtuMap[id]);
    }, [rtuMap, selectedNodeKeys]);

      const formatDateTime = (value: string | null | undefined) => formatDisplayDateTime(value);

      const dateTemplate = (rowData: any) => formatDateTime(rowData.lastData);

    const diskUsageBodyTemplate = (rowData:any) => {
      return `${rowData.diskUsage.toFixed(1)}%`;
    };
    const cpuUsageBodyTemplate = (rowData:any) => {
      return `${rowData.cpuUsage.toFixed(1)}%`;
    };
    const getSeverity = (status:any) => {    
        return getStatusColor(status);
    };

    const statusTooltip = (rowData: RTU) => {
        const source = humanizeStatusSource(rowData.statusSource);
        const reason = rowData.statusReason ? ` - ${humanizeStatusReason(rowData.statusReason)}` : '';
        const statusLabel = t(getStatusLabelKey(rowData.status), { defaultValue: String(rowData.status ?? '') });

        return `${statusLabel} (${source})${reason}`;
    };

    const statusItemTemplate = (option: any) => {
        return (
            <Tag
                className="asset-status-filter-tag"
                value={option.label}
                style={{ backgroundColor: getSeverity(option.value) }}
            />
        );
    };

    const statusValueTemplate = (option: any) => {
        if (!option) {
            return <span />;
        }

        return (
            <Tag
                className="asset-status-filter-tag asset-status-filter-tag--value"
                value={option.label}
                style={{ backgroundColor: getSeverity(option.value) }}
            />
        );
    };

    const statusBodyTemplate = (rowData:any) => {
        const displayStatus = rowData.systemRestart === true ? 'rebooting' : rowData.status;
        const tooltip = statusTooltip(rowData as RTU);

        return (
            <span title={tooltip} aria-label={tooltip}>
                <Avatar style={{ backgroundColor: getSeverity(displayStatus), width:'20px', height:'20px'}} shape="circle" />
            </span>
        );
    };
    const rowButtonTemplate = (rowData: any) => {
        if (!rowData.children || rowData.children.length === 0) {
            // Only show the button for leaf nodes (RTUs)
            return (
                <div style={{ display: 'flex', justifyContent: 'center' }} onClick={(event) => event.stopPropagation()}>
                    <RTUActions
                        mode="row"
                        selectedRTUIp={rowData.data.publicIPAddress}
                        selectedRows={[rowData.data]}
                    />
                </div>
            );
        }
        return null;
    };
//     const nodeTemplate = (node: any, column: string) => {
//       if (!node.data.id) {
//           return node.data[column];
//       }
//       switch (column) {
//           case 'name':
//               return (
//                   <span
//                       style={{ cursor: 'pointer', color: 'blue' }}
//                       onClick={() => navigate(`/rtupage/${node.data.id}`)}
//                   >
//                       {node.data.name}
//                   </span>
//               );
//           case 'status':
//               return statusBodyTemplate(node.data);
//           case 'diskUsage':
//               return diskUsageBodyTemplate(node.data);
//           case 'cpuUsage':
//               return cpuUsageBodyTemplate(node.data);
//           default:
//               return node.data[column];
//       }
//   };

const nodeTemplate = (node: any, column: string) => {
    const depth = node.key.split('-').length - 1; // Calculate depth based on key

    const style = {
        textAlign: depth < 2 ? 'left' as const : 'center' as const, // Align left for first two levels
        paddingLeft: depth === 1 ? '20px' : '0px', // Add padding for level 2 (IGAPO)
    };

    if (!node.data.id) {
        return node.data[column];
    }

    switch (column) {
        case 'provider':
            return <span style={style} />;
        case 'name':
            return (
                <span
                    style={{ ...style, cursor: 'pointer', color: 'blue' }}
                    onClick={() => navigate(`/rtupage/${node.data.id}`)}
                >
                    {node.data.name}
                </span>
            );
        case 'status':
            return statusBodyTemplate(node.data);
        case 'diskUsage':
            return diskUsageBodyTemplate(node.data);
        case 'cpuUsage':
            return cpuUsageBodyTemplate(node.data);
        case 'statusObservedAtUtc':
            return <span style={style}>{formatDateTime(node.data.statusObservedAtUtc)}</span>;
        case 'lastFileReceivedAtUtc':
            return <span style={style}>{formatDateTime(node.data.lastFileReceivedAtUtc)}</span>;
        case 'lastData':
            return <span style={style}>{formatDateTime(node.data.lastData)}</span>;
        case 'installDate':
            return <span style={style}>{formatDateTime(node.data.installDate)}</span>;
        case 'reportingToServer':
            return <span style={style}>{formatDateTime(node.data.reportingToServer)}</span>;
        case 'lastConfigUpdate':
            return <span style={style}>{formatDateTime(node.data.lastConfigUpdate)}</span>;
        default:
            return <span style={style}>{node.data[column]}</span>;
    }
};


    const statusFilterElement = (
        <Dropdown
            value={statusFilterValue}
            options={statusOptions}
            onChange={(e: any) => setStatusFilterValue(e.value ?? null)}
            itemTemplate={statusItemTemplate}
            valueTemplate={statusValueTemplate}
            optionLabel="label"
            optionValue="value"
            placeholder=""
            className="p-column-filter asset-status-filter-dropdown"
            panelClassName="asset-status-filter-panel"
            showClear
            style={{ width: '100%', minWidth: '4rem' }}
        />
    );

    const [columns, setColumns] = useState([  
    //   { field: 'provider', header: 'Producer', headerClassName: 'custom-header-provider', visible: true, filter: true, filterElement: null, body: null, expander: true, filterMatchMode: 'contains' },
      { field: 'id', header: t('assetList.columns.id'), headerClassName: 'custom-header-id', visible: true, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains'},
      { field: 'status', header: t('assetList.columns.status'), headerClassName: 'custom-header-status', visible: true, filter: true, filterElement: null, body: statusBodyTemplate, expander: false, filterMatchMode: 'equals' },
      { field: 'name', header: t('assetList.columns.name'), headerClassName: 'custom-header-name', visible: true, filter: true, filterElement: null, body: nodeTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'diskUsage', header: t('assetList.columns.diskUsage'), headerClassName: 'custom-header-disk-usage', visible: true, filter: true, filterElement: null, body: diskUsageBodyTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'cpuUsage', header: t('assetList.columns.cpuUsage'), headerClassName: 'custom-header-cpu-usage', visible: true, filter: true, filterElement: null, body: cpuUsageBodyTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'statusObservedAtUtc', header: t('assetList.columns.lastChecked', { defaultValue: 'Last Checked' }), headerClassName: 'custom-header-last-checked', visible: true, filter: true, filterElement: null, body: dateTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'lastFileReceivedAtUtc', header: t('assetList.columns.lastDataReceived', { defaultValue: 'Last Data Received' }), headerClassName: 'custom-header-last-data-received', visible: true, filter: true, filterElement: null, body: dateTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'location', header: t('assetList.columns.location'), headerClassName: 'custom-header-location', visible: true, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'configVersion', header: t('assetList.columns.configVersion'), headerClassName: 'custom-header-config-version', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'deploymentVersion', header: t('assetList.columns.deploymentVersion'), headerClassName: 'custom-header-deployment-version', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'lastData', header: t('assetList.columns.lastData'), headerClassName: 'custom-header-last-data', visible: false, filter: true, filterElement: null, body: dateTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'installDate', header: t('assetList.columns.installDate'), headerClassName: 'custom-header-install-date', visible: false, filter: true, filterElement: null, body: dateTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'lastConfigUpdate', header: t('assetList.columns.lastConfigUpdate'), headerClassName: 'custom-header-last-config-update', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'publicIPAddress', header: t('assetList.columns.publicIp'), headerClassName: 'custom-header-public-ipaddress', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'reportingToServer', header: t('assetList.columns.reportingToServer'), headerClassName: 'custom-header-reportingtoserver', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'modemIP', header: t('assetList.columns.modemIp'), headerClassName: 'custom-header-public-ipaddress', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'ethernetIP', header: t('assetList.columns.ethernetIp'), headerClassName: 'custom-header-public-ipaddress', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'groupId', header: t('assetList.columns.groupId'), headerClassName: 'custom-header-group-id', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' }
  ]);
  

    const onColumnsChange = useCallback((field:any, isChecked:any) => {
        setColumns((currentColumns) => {
            const visibleCount = currentColumns.filter((col) => col.visible).length;
            const isLastVisibleColumn =
                !isChecked && visibleCount === 1 && currentColumns.find((col) => col.field === field)?.visible;
            if (isLastVisibleColumn) {
                return currentColumns;
            }
            return currentColumns.map(col => {
                if (col.field === field) {
                    return { ...col, visible: isChecked };
                }
                return col;
            });
        });
    }, []);

    const handleSelectionChange = useCallback((e: any) => {
        const nextKeys = e.value ?? null;
        setSelectedNodeKeys(nextKeys);
    }, []);

    const op:any = useRef(null);
    const renderHeader = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '0.6rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                    {showFilters && (
                        <div style={{ display: 'flex', justifyContent: 'center', maxWidth: '200px' }}>
                            <span className="p-input-icon-left" style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
                                <i className="pi pi-search" style={{ fontSize: '0.7rem', left: '0.5rem' }} />
                                <InputText 
                                    type="search" 
                                    value={globalFilter}
                                    onInput={(e) => setGlobalFilter((e.target as HTMLInputElement).value)} 
                                    placeholder="" 
                                    style={{ fontSize: '0.75rem', height: '1.5rem', paddingLeft: '1.5rem', width: '100%' }}
                                />
                            </span>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-end', marginLeft: 'auto', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i
                            className="pi pi-search"
                            style={{ cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: showFilters ? defaultThemeColor : '#333', padding: '0.25rem' }}
                            title={showFilters ? t('search.hide') : t('search.show')}
                            onClick={toggleFilters}
                        />
                        <i
                            className="pi pi-list"
                            style={{ cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#333', padding: '0.25rem' }}
                            title={t('table.selectColumns')}
                            onClick={(e) => op.current.toggle(e)}
                        />
                        <OverlayPanel ref={op} closeOnEscape dismissable={true} className="asset-column-picker">
                            {columns.map((col) => (
                                <div key={col.field} className="asset-column-picker__row">
                                    <Checkbox
                                        inputId={`cb-${col.field}`}
                                        checked={col.visible}
                                        onChange={(e) => onColumnsChange(col.field, e.checked)}
                                        className="asset-column-picker__checkbox"
                                    />
                                    <label htmlFor={`cb-${col.field}`} className="asset-column-picker__label">{col.header}</label>
                                </div>
                            ))}
                        </OverlayPanel>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', minWidth: 'max-content' }}>
                        <RTUActions mode="bulk" selectedRows={selectedRows} />
                    </div>
                </div>
            </div>
        );
    };

    const header = renderHeader();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchRTUs({}));
      }, [dispatch]);

    useEffect(() => {
        setColumns((currentColumns) =>
            hydrateColumnVisibility(currentColumns, columnStorageKeyRef.current)
        );
    }, []);

    useEffect(() => {
        if (!hasHydratedColumnsRef.current) {
            hasHydratedColumnsRef.current = true;
            return;
        }
        persistColumnVisibility(columns, columnStorageKeyRef.current);
    }, [columns]);

    // Columns that should be centered (numeric, percentage, status, icons)
    const centerCols = ['status', 'id', 'diskUsage', 'cpuUsage', 'groupId'];

    return (
        <div className="card" style={{ padding: '0.25rem', overflow: 'hidden' }}>
            <h1 className="dms-visually-hidden">{t('navigation.groupedAssets')}</h1>
            <TreeTable
                value={treeData}
                resizableColumns
                columnResizeMode="expand"
                header={header}
                paginator
                rows={25}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                paginatorLeft={<PaginatorRowsLabel />}
                paginatorTemplate={STANDARD_PAGINATOR_TEMPLATE}
                currentPageReportTemplate={STANDARD_PAGE_REPORT_TEMPLATE}
                paginatorClassName="compact-paginator"
                showGridlines
                removableSort
                filterMode={filterMode}
                selectionMode="checkbox"
                selectionKeys={selectedNodeKeys}
                onSelectionChange={handleSelectionChange}
                style={{ width: '100%' }}
                tableStyle={{ tableLayout: 'auto', width: 'auto', minWidth: '100%' }}
                emptyMessage={loading ? (
                    <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
                        <SkeletonTree rows={10} columns={[0.7, 0.6, 1.2, 0.7, 0.7, 1, 1, 1.4]} />
                    </SkeletonRegion>
                ) : undefined}
            >
                <Column 
                    key="provider" 
                    field="provider" 
                    header={t('assetList.columns.provider')} 
                    expander 
                    filter={showFilters}
                    showFilterMenu={false}
                    style={{ whiteSpace: 'nowrap' }}
                    body={(node) => nodeTemplate(node, 'provider')} 
                />    
                {columns.filter(col => col.visible).map(col => {
                    const isCenter = centerCols.includes(col.field);
                    
                    return (
                        <Column 
                            key={col.field} 
                            field={col.field} 
                            header={col.header} 
                            expander={col.expander}
                            filterElement={col.field === 'status' ? statusFilterElement : col.filterElement}
                            style={{ whiteSpace: 'nowrap' }}
                            bodyStyle={{ textAlign: isCenter ? 'center' : 'left' }} 
                            filter={showFilters} 
                            showFilterMenu={false}
                            sortable 
                            headerClassName={col.headerClassName}
                            filterMatchMode={col.filterMatchMode}  
                            body={(node: any) => nodeTemplate(node, col.field)}
                        />
                    );
                })}
                <Column 
                    body={rowButtonTemplate} 
                    filter={showFilters} 
                    filterElement={() => <span></span>}
                    header={t('table.actions')} 
                    headerClassName="grouped-assets-actions-column"
                    bodyClassName="grouped-assets-actions-column"
                    style={{ width: '148px', minWidth: '148px', maxWidth: '148px' }} 
                    headerStyle={{ textAlign: 'center' }}
                    bodyStyle={{ textAlign: 'center', overflow: 'visible' }}
                />
            </TreeTable>
        </div>
    );
};

export default RTUTreeList;
